import { useSnapshot } from 'valtio'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { mapState, setDirectionsTo, setZoom } from '../../../store/mapState.ts'

let activePolylines: google.maps.Polyline[] = []

export const Directions: React.FC = () => {
    const map = (useSnapshot(mapState) as typeof mapState).map
    const myPosition = (useSnapshot(mapState) as typeof mapState).myPosition
    const directionsTo: google.maps.LatLng | null = (useSnapshot(mapState) as typeof mapState).directionsTo
    const travelMode = (useSnapshot(mapState) as typeof mapState).travelMode
    const [show, setShow] = React.useState(false)
    const [steps, setSteps] = React.useState<any[]>([])

    React.useEffect(() => {
        return () => {
            setTimeout(() => onClose())
        }
    }, [])

    const doRoute = async (myPosition: google.maps.LatLng, directionsTo: google.maps.LatLng) => {
        removeRouteVisuals()
        if (!map) return

        const listener = window.google.maps.event.addListener(map, 'idle', () => {
            const zoom = map?.getZoom()
            setZoom(zoom!)
            window.google.maps.event.removeListener(listener)
        })

        try {
            const routesLib: any = await google.maps.importLibrary('routes')
            const Route = routesLib.Route

            const request = {
                origin: myPosition,
                destination: directionsTo,
                travelMode: travelMode?.toUpperCase() || 'DRIVING',
                routeModifiers: {
                    avoidTolls: true,
                    avoidHighways: true,
                },
                fields: ['path', 'legs'],
            }

            const { routes } = await Route.computeRoutes(request)

            if (routes && routes.length > 0) {
                const route = routes[0]

                activePolylines = route.createPolylines()
                activePolylines.forEach((p: google.maps.Polyline) => p.setMap(map))

                // Fit map to route bounds
                const bounds = new google.maps.LatLngBounds()
                activePolylines.forEach((p: google.maps.Polyline) => {
                    p.getPath().forEach((point: google.maps.LatLng) => bounds.extend(point))
                })
                map.fitBounds(bounds)

                // Extract step instructions for the panel
                const legs = route.toJSON?.()?.legs || []
                const allSteps: any[] = []
                legs.forEach((leg: any) => {
                    leg.steps?.forEach((step: any) => {
                        if (step.navigationInstruction?.instructions) {
                            allSteps.push({
                                instruction: step.navigationInstruction.instructions,
                                distance: step.localizedValues?.distance?.text || '',
                                duration: step.localizedValues?.duration?.text || '',
                            })
                        }
                    })
                })
                setSteps(allSteps)
                setShow(true)
            } else {
                window.alert('Directions are not available')
                onClose()
            }
        } catch (e: any) {
            window.alert(e.message || 'Route request failed')
            onClose()
        }
    }

    const removeRouteVisuals = () => {
        activePolylines.forEach((p: google.maps.Polyline) => p.setMap(null))
        activePolylines = []
    }

    const onClose = () => {
        setDirectionsTo(null)
        mapState.travelMode = null as any
        removeRouteVisuals()
        setSteps([])
        setShow(false)
    }

    React.useEffect(() => {
        if (myPosition && directionsTo && travelMode) {
            void doRoute(myPosition, directionsTo)
        }
    }, [myPosition, directionsTo])

    const controls = (
        <div id='directions-header' className={'w-full flex flex-row justify-between bg-primary text-primary-content'}>
            <div className={'px-2'}>
                <h4 className={'mb-0'}>{travelMode} Directions</h4>
            </div>
            <div
                className={'px-2 cursor-pointer'}
                onClick={() => onClose()}>
                <FontAwesomeIcon icon={faClose} fontSize={'medium'}/>
            </div>
        </div>
    )

    return (
        <div
            className={`${show ? 'inline-block' : 'hidden'} absolute top-[8px] left-[25vw] w-[50vw] z-47 bg-primary-content`}
            style={{zIndex: 1}}
        >
            {controls}
            <div id="directions-panel" style={{maxHeight: '40vh', overflowY: 'auto', padding: '0.25rem'}}>
                {steps.map((step, i) => (
                    <div key={i} className={'flex items-start gap-2 p-2 border-b border-base-300'}>
                        <span className={'text-sm font-semibold text-primary min-w-[1.5rem]'}>{i + 1}.</span>
                        <div className={'flex-1'}>
                            <div className={'text-sm'} dangerouslySetInnerHTML={{__html: step.instruction}}/>
                            <div className={'text-xs text-neutral-content/60'}>{step.distance} · {step.duration}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}